// components/omni-radio-button/src/omni-radio-button.js
import { BaseElement, FormAssociated, css, html, define, upgradeProperties, emitValueEvents } from '../../../core/index.js';

const TAG_NAME = 'omni-radio-button';
const selectorForName = (name) => `${TAG_NAME}[name="${CSS.escape(name)}"]`;

const styles = css`

:host {
	display:inline-flex;
	--iam-rb-bg: var(--white, #fff);
	--iam-rb-bg-checked: var(--primary-color, #00C0FB);
	--iam-rb-border: var(--primary-color, #00C0FB);
	--iam-rb-border-checked: var(--primary-color, #00C0FB);
	--iam-rb-text: var(--black, #1D252C);
	--iam-rb-text-checked: var(--white, #fff);
	--iam-rb-radius: .5rem;
	--iam-rb-padding: .45rem .75rem;
	--iam-rb-gap: .35rem;
	--iam-rb-inline-gap: .25rem;
	vertical-align: middle;
	user-select: none;
	outline: none;
}

button {
	all: unset;
	display:inline-flex;
	align-items:center;
	gap: var(--iam-rb-gap);
	padding: var(--iam-rb-padding);
	border: 1px solid var(--iam-rb-border);
	border-radius: var(--iam-rb-radius);
	background: var(--iam-rb-bg);
	color: var(--iam-rb-text);
	cursor: pointer;
	transition: background .15s ease, border-color .15s ease, box-shadow .15s ease;
}

:host([disabled]) button { cursor: not-allowed; opacity:.6; }

:host([checked]) button {
	background: var(--iam-rb-bg-checked);
	border-color: var(--iam-rb-border-checked);
	color: var(--iam-rb-text-checked);
}

button:focus-visible {
	box-shadow: 0 0 0 3px rgba(0, 120, 255, .35);
}

.label { white-space: nowrap; }
`;

class OmniRadioButton extends FormAssociated(BaseElement) {
	static formAssociated = super.formAssociated;
	static get observedAttributes(){ return ['name','value','checked','disabled']; }
	static shadowStyleSheets = [styles];

	// ---- PRIVATE CLASS STATE (no globals) ------------------------------------
	static #timers  = new Map();      // groupKey -> rAF id
	static #formIds = new WeakMap();  // form -> stable id
	static #formSeq = 0;

	static #formId(form){
		if(!form) return 'noform';
		if(!this.#formIds.has(form)) this.#formIds.set(form, 'f' + (++this.#formSeq));
		return this.#formIds.get(form);
	}
	static #groupKey(el){
		const form = el._getForm?.() ?? el.closest('form') ?? null;
		return `${this.#formId(form)}::${el.name || ''}`;
	}
	static #scheduleGroupUpdate(el){
		const k = this.#groupKey(el);
		if(this.#timers.has(k)) cancelAnimationFrame(this.#timers.get(k));
		this.#timers.set(k, requestAnimationFrame(()=>{
			this.#timers.delete(k);
			this.#updateGroup(el);
		}));
	}
	static #siblings(el){
		const name = el.name;
		if(!name) return [];
		const form = el._getForm?.() ?? el.closest('form') ?? null;
		const selector = selectorForName(name);
		if(!selector) return [];
		return Array.from(document.querySelectorAll(selector))
			.filter(x => ((x._getForm?.() ?? x.closest('form') ?? null) === form));
	}
	static #inlineGapFor(el){
		const parent = el.parentElement;
		if (!parent) return 'var(--iam-rb-inline-gap, 0.25rem)';
		const styles = getComputedStyle(parent);
		const display = styles.display;
		if (!display.includes('flex') && !display.includes('grid')) {
			return 'var(--iam-rb-inline-gap, 0.25rem)';
		}
		const gap = styles.columnGap;
		return gap && gap !== 'normal' ? gap : '0px';
	}
	static #updateGroup(el){
		const all = this.#siblings(el);
		if(all.length === 0) return;

		// DOM order
		all.sort((a,b)=> (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1);

		all.forEach((btn, i) => {
			const first = i === 0;
			const last  = i === all.length - 1;
			const radius = first && last ? '.5rem'
				: first ? '.5rem 0 0 .5rem'
						: last  ? '0 .5rem .5rem 0'
							: '0';
			btn.style.setProperty('--iam-rb-radius', radius);
			btn.style.marginLeft = first ? '0' : `calc(-1px - ${this.#inlineGapFor(btn)})`;
		});
	}

	// ---- INSTANCE -------------------------------------------------------------
	constructor(){
		super();
		upgradeProperties(this, ['checked', 'disabled', 'name', 'value']);

		// FACE: be defensive (Firefox/iframes/double attach)
		this._supportsFACE = !!this._internals; // provided by FormAssociated mixin if attachInternals works

		this._hiddenInput = this._supportsFACE ? null : document.createElement('input');
		if (this._hiddenInput) this._hiddenInput.type = 'hidden';

		// initial render
		this._root.append(
			html`
				<button part="button" type="button" aria-checked="false" role="radio">
					<span class="label" part="label"><slot></slot></span>
				</button>
			`
		);
		this._btn = this._root.querySelector('button');
		this._slot = this._root.querySelector('slot');
		this._slot?.addEventListener('slotchange', () => {
			if (!this.hasAttribute('value')) {
				this._syncFormValue();
			}
		});

		this.addEventListener('click', ()=>{
			if(this.disabled) return;
			this._handleClick();
		});
		this.addEventListener('keydown', (e)=>{
			if(this.disabled) return;
			const k = e.key;
			if(k === ' ' || k === 'Enter'){
				e.preventDefault();
				this._handleClick();
			} else if (['ArrowRight','ArrowDown','ArrowLeft','ArrowUp','Home','End'].includes(k)){
				e.preventDefault();
				this._moveFocusAndSelect(k);
			}
		});

		// default focusability (we manage roving on the inner button)
		this.tabIndex = 0;

		this._reflectA11y();
	}

	// ---- lifecycle ------------------------------------------------------------
	connectedCallback(){
		super.connectedCallback?.();

		// Fallback proxy input: keep it in the same form if possible (helps legacy selectors)
		if(this._hiddenInput && !this._hiddenInput.isConnected){
			const f = this._getForm();
			if (f) f.appendChild(this._hiddenInput);
			else this.after(this._hiddenInput);
		}
		this._syncFormValue();
		this.constructor.#scheduleGroupUpdate(this);
		requestAnimationFrame(() => this._updateGroupTabStops());
	}

	disconnectedCallback(){
		this.constructor.#scheduleGroupUpdate(this);
	}

	attributeChangedCallback(name){
		super.attributeChangedCallback?.(name);
		switch(name){
			case 'checked': this._onCheckedChanged(); break;
			case 'disabled': this._reflectA11y(); break;
			case 'name':
			case 'value': this._syncFormValue(); break;
		}
		if (name === 'name') {
			this.constructor.#scheduleGroupUpdate(this);
			requestAnimationFrame(() => this._updateGroupTabStops());
		}
	}

	// ---- properties -----------------------------------------------------------
	get name(){ return this.getAttribute('name') || ''; }
	set name(v){ v == null ? this.removeAttribute('name') : this.setAttribute('name', String(v)); }

	get value(){
		const attr = this.getAttribute('value');
		if (attr != null) return attr;
		return this._getLabelText();
	}
	set value(v){ v == null ? this.removeAttribute('value') : this.setAttribute('value', String(v)); }

	get checked(){ return this.hasAttribute('checked'); }
	set checked(v){ v ? this.setAttribute('checked','') : this.removeAttribute('checked'); }

	get disabled(){ return this.hasAttribute('disabled'); }
	set disabled(v){ v ? this.setAttribute('disabled','') : this.removeAttribute('disabled'); }

	// ---- FACE helpers ---------------------------------------------------------
	get type(){ return 'radio'; } // for compatibility with your previous code
	formResetCallback(){ this.checked = false; }
	formStateRestoreCallback(state/*,mode*/){ this.checked = !!state; }

	_getForm(){ return this.form ?? this.closest('form') ?? null; }

	// ---- behavior -------------------------------------------------------------
	_handleClick(){
		const was = this.checked;
		if(was) this.checked = false;   // allow unselect
		else    this._selectExclusively();

		this._emitInputChange();
	}

	_selectExclusively(){
		this._getGroup().forEach(el => { if(el !== this) el.checked = false; });
		this.checked = true;
	}

	_getGroup(){
		const name = this.name;
		if(!name) return [this];
		const selector = selectorForName(name);
		if(!selector) return [this];
		const all = Array.from(document.querySelectorAll(selector));
		const myForm = this._getForm();
		return all.filter(el => ((el._getForm?.() ?? el.closest('form') ?? null) === myForm));
	}

	_moveFocusAndSelect(key){
		const group = this._getGroup();
		if(group.length <= 1) return;

		const idx = group.indexOf(this);
		const dir = (key === 'ArrowRight' || key === 'ArrowDown') ? 1
			: (key === 'ArrowLeft'  || key === 'ArrowUp')   ? -1 : 0;

		let target = null;
		if(key === 'Home') target = group[0];
		else if(key === 'End') target = group[group.length - 1];
		else if(dir) target = group[(idx + dir + group.length) % group.length];

		if(target){
			target._selectExclusively();
			target.focus();
			target._emitInputChange();
		}
	}

	_onCheckedChanged(){
		this._reflectA11y();
		this._syncFormValue();
		this.constructor.#scheduleGroupUpdate(this);
		requestAnimationFrame(() => this._updateGroupTabStops());
	}

	_reflectA11y(){
		const checked  = this.checked;
		const disabled = this.disabled;
		this._btn.setAttribute('aria-checked', String(checked));
		this._btn.setAttribute('aria-disabled', String(disabled));
		this._btn.tabIndex = disabled ? -1 : 0;
		this.toggleAttribute('checked', checked);
		this.toggleAttribute('disabled', disabled);
	}

	_getLabelText(){
		const assigned = this._slot?.assignedNodes?.({ flatten: true }) ?? [];
		if (assigned.length){
			const text = assigned.map(node => node.textContent ?? '').join('').trim();
			if (text) return text;
		}
		return (this.textContent ?? '').trim();
	}

	_syncFormValue(){
		// Provided by FormAssociated mixin: this._internals and this._updateFormValue
		if (this._internals) {
			this._updateFormValue(this.checked ? this.value : null);
		} else {
			this.#syncHiddenInput();
		}
	}

	#syncHiddenInput(){
		const hi = this._hiddenInput;
		if(!hi) return;
		if(this.checked && this.name){
			hi.disabled = !!this._internals; // disabled if FACE exists (avoid double submit)
			hi.name  = this.name;
			hi.value = this.value ?? '';
		} else {
			hi.name = '';
			hi.value = '';
		}
	}

	_emitInputChange(){
		emitValueEvents(this);
	}

	_updateGroupTabStops(){
		const group = this._getGroup();
		if(group.length === 0) return;

		// Only operate on siblings that have finished constructing (_btn exists)
		const ready = group.filter(el => el && el._btn);
		const pending = group.length - ready.length;

		// If not all siblings are upgraded yet, try again next frame
		if (pending > 0) {
			requestAnimationFrame(() => this._updateGroupTabStops());
		}

		if (ready.length === 0) return;

		const active = ready.find(x => x.checked && !x.disabled) || ready[0];
		ready.forEach(el => {
			el._btn.tabIndex = (el === active && !el.disabled) ? 0 : -1;
		});
	}
}

define(TAG_NAME, OmniRadioButton);

export { OmniRadioButton };
